import { useState } from 'react'
import { api, errorText } from '../api/client'
import type { Condition, Loan, NewDeal, Option } from '../api/types'
import { LOAN_TYPES, PURPOSES } from '../lib/constants'
import { shortDate } from '../lib/format'
import { DateField } from './DateField'
import { Icon } from './Icon'
import { Modal } from './Modal'
import { useConfirm } from './Confirm'
import { useToast } from './Toast'

/** The deal's own date columns, paired with the LOS date keys the server maps them
 *  onto — so the milestone rail reads the same before and after LendingPad. */
type MilestoneField =
  | 'application_at' | 'preapproval_at' | 'contract_at' | 'appraisal_at'
  | 'lock_expires_at' | 'closing_at' | 'funded_at'

const MILESTONES: { field: MilestoneField; key: string; label: string }[] = [
  { field: 'application_at', key: 'application_taken', label: 'Application taken' },
  { field: 'preapproval_at', key: 'pre_approval', label: 'Pre-approved' },
  { field: 'contract_at', key: 'contract_accepted', label: 'Contract accepted' },
  { field: 'appraisal_at', key: 'appraisal_received', label: 'Appraisal received' },
  { field: 'lock_expires_at', key: 'lock_expiration', label: 'Lock expires' },
  { field: 'closing_at', key: 'estimated_closing', label: 'Estimated closing' },
  { field: 'funded_at', key: 'funded', label: 'Funded' },
]

// LendingPad's own vocabulary, not a CRM one: a deal keeps the words it will have
// once the LOS owns the file, so nothing has to be re-typed at hand-off.
const OCCUPANCIES = ['primary_residence', 'second_home', 'investment', 'other']
const PROPERTY_TYPES = [
  'detached', 'attached', 'condominium', 'cooperative', 'pud',
  'manufactured_home', 'modular_or_prefabricated_home',
]

/** An ISO timestamp → what <input type="date"> wants. The server echoes back the
 *  day it stored, so the first ten characters round-trip unchanged. */
const dayValue = (iso: string | undefined) => (iso ? iso.slice(0, 10) : '')

const label = (options: readonly Option[], key: string | null | undefined) =>
  options.find((o) => o.key === key)?.label

/** Every editable field of a pre-LOS opportunity in one form. Money and rate go up
 *  as typed ("$450,000", "6.25%") — the server parses and range-checks them, so the
 *  officer sees one rejection message rather than two spellings of the same rule. */
export function DealEditor({ loan, dealId, sources, onClose, onSaved }: {
  loan: Loan
  dealId: number
  sources: Option[]
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState<NewDeal>({
    borrower_name: loan.borrower ?? '',
    co_borrower_name: loan.co_borrower_name ?? '',
    purpose: loan.purpose ?? '',
    loan_type: loan.loan_type ?? '',
    loan_amount: loan.loan_amount ?? '',
    purchase_price: loan.purchase_price ?? '',
    down_payment: loan.down_payment ?? '',
    note_rate: loan.note_rate ?? '',
    credit_score: loan.credit_score ?? '',
    term_months: loan.term_months ?? '',
    occupancy: loan.occupancy ?? '',
    property_type: loan.property_type ?? '',
    // Only a source the server still offers: anything else (an old key, or the
    // record's own origin) would come back as "unknown lead source" on save.
    source: sources.some((s) => s.key === loan.source) ? loan.source ?? '' : '',
    property_street: loan.property_street ?? '',
    property_city: loan.property_city ?? '',
    property_state: loan.property_state ?? '',
    property_zip: loan.property_zip ?? '',
    notes: loan.notes ?? '',
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const toast = useToast()

  const set = (k: keyof NewDeal) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const canSave = Boolean(String(form.borrower_name ?? '').trim()) && !busy

  async function save() {
    if (!canSave) return
    setBusy(true)
    setError('')
    try {
      await api.updateDeal(dealId, form)
      toast('Loan file updated', { tone: 'positive' })
      onSaved()
      onClose()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit loan file"
      subtitle="The CRM owns this file until the loan system opens one. Milestone dates live on the Milestones tab."
      width={620}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={save} disabled={!canSave}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      }
    >
      <form className="form-grid" onSubmit={(e) => { e.preventDefault(); save() }}>
        <label className="fl"><span>Borrower</span>
          <input className="field" value={form.borrower_name} onChange={set('borrower_name')} /></label>
        <label className="fl"><span>Co-borrower <span className="hint">— if any</span></span>
          <input className="field" value={form.co_borrower_name} onChange={set('co_borrower_name')} /></label>
        <label className="fl"><span>Purpose</span>
          <select className="field" value={form.purpose} onChange={set('purpose')}>
            <option value="">—</option>
            {PURPOSES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select></label>
        <label className="fl"><span>Loan type</span>
          <select className="field" value={form.loan_type} onChange={set('loan_type')}>
            <option value="">—</option>
            {LOAN_TYPES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select></label>
        <label className="fl"><span>Loan amount</span>
          <input className="field mono" inputMode="decimal" placeholder="$" value={form.loan_amount} onChange={set('loan_amount')} /></label>
        <label className="fl"><span>{form.purpose === 'purchase' ? 'Purchase price' : 'Estimated value'}</span>
          <input className="field mono" inputMode="decimal" placeholder="$" value={form.purchase_price} onChange={set('purchase_price')} /></label>
        <label className="fl"><span>Down payment</span>
          <input className="field mono" inputMode="decimal" placeholder="$" value={form.down_payment} onChange={set('down_payment')} /></label>
        <label className="fl"><span>Rate <span className="hint">— if quoted</span></span>
          <input className="field mono" inputMode="decimal" placeholder="%" value={form.note_rate} onChange={set('note_rate')} /></label>
        <label className="fl"><span>Term <span className="hint">— months</span></span>
          <input className="field mono" inputMode="numeric" placeholder="360" value={form.term_months} onChange={set('term_months')} /></label>
        <label className="fl"><span>Credit score</span>
          <input className="field mono" inputMode="numeric" placeholder="740" value={form.credit_score} onChange={set('credit_score')} /></label>
        <label className="fl"><span>Occupancy</span>
          <select className="field" value={form.occupancy} onChange={set('occupancy')}>
            <option value="">—</option>
            {OCCUPANCIES.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
          </select></label>
        <label className="fl"><span>Property type</span>
          <select className="field" value={form.property_type} onChange={set('property_type')}>
            <option value="">—</option>
            {PROPERTY_TYPES.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
          </select></label>
        <label className="fl span-2"><span>Where the lead came from</span>
          <select className="field" value={form.source} onChange={set('source')}>
            <option value="">—</option>
            {sources.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select></label>
        <label className="fl span-2"><span>Property address</span>
          <input className="field" value={form.property_street} onChange={set('property_street')} placeholder="Street, or leave blank if still shopping" /></label>
        <div className="span-2" style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.5fr 0.8fr', gap: 10 }}>
          <label className="fl"><span>City</span><input className="field" value={form.property_city} onChange={set('property_city')} /></label>
          <label className="fl"><span>State</span><input className="field" value={form.property_state} onChange={set('property_state')} maxLength={2} /></label>
          <label className="fl"><span>ZIP</span><input className="field" value={form.property_zip} onChange={set('property_zip')} maxLength={10} /></label>
        </div>
        <label className="fl span-2"><span>Notes</span>
          <textarea className="field" rows={3} value={form.notes} onChange={set('notes')} /></label>
        {error ? <div className="notice danger span-2"><span>{error}</span></div> : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}

/** Milestone dates, saved one at a time as they are picked: an officer updating a
 *  closing date should not have to find and press Save somewhere else. */
export function DealMilestones({ dealId, dates, onSaved }: {
  dealId: number
  dates: Record<string, string> | null
  onSaved: () => void
}) {
  const [busy, setBusy] = useState<MilestoneField | null>(null)
  const toast = useToast()

  async function save(field: MilestoneField, day: string) {
    setBusy(field)
    const patch: NewDeal = {}
    patch[field] = day || null
    try {
      await api.updateDeal(dealId, patch)
      onSaved()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      {MILESTONES.map((m) => (
        <div className="kvrow" key={m.field}>
          <span className="k">{m.label}</span>
          <DateField label={m.label} value={dayValue(dates?.[m.key])} onChange={day => void save(m.field, day)} disabled={busy === m.field} clearable style={{ width: 200, maxWidth: '100%' }} />
        </div>
      ))}
      <p className="quiet" style={{ marginTop: 12 }}>
        These fill the rail at the top of the file. The loan system's own dates take over once it has the loan.
      </p>
    </>
  )
}

const REQUESTED = { icon: 'error', color: 'var(--danger)', badge: 't-danger' }

const STATUSES: Record<string, { icon: string; color: string; badge: string }> = {
  requested: REQUESTED,
  received: { icon: 'schedule', color: 'var(--warning)', badge: 't-warning' },
  cleared: { icon: 'check_circle', color: 'var(--positive)', badge: 't-positive' },
  waived: { icon: 'do_not_disturb_on', color: 'var(--ink-4)', badge: 't-neutral' },
}

/** The conditions an officer chases on a pre-LOS file: add, move along, annotate,
 *  delete. Every mutation returns the new list, but we re-read the loan instead —
 *  the open count, the risk read and the rail all hang off the same payload. */
export function DealConditions({ dealId, items, common, statuses, onChanged, onChase }: {
  dealId: number
  items: [string, Condition][]
  common: string[]
  statuses: Option[]
  onChanged: () => void
  onChase: (name: string) => void
}) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [noting, setNoting] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const toast = useToast()
  const confirm = useConfirm()

  async function run(work: Promise<unknown>) {
    setBusy(true)
    try {
      await work
      onChanged()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  function add(what: string) {
    const trimmed = what.trim()
    if (!trimmed || busy) return
    setName('')
    run(api.addCondition(dealId, { name: trimmed }))
  }

  function saveNote(conditionId: number) {
    setNoting(null)
    run(api.updateCondition(conditionId, { notes: note }))
  }

  const taken = new Set(items.map(([k]) => k.toLowerCase()))
  const unused = common.filter((c) => !taken.has(c.toLowerCase()))

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: unused.length ? 10 : 16 }}>
        <input
          className="field"
          style={{ flex: 1 }}
          placeholder="What are you asking the borrower for?"
          maxLength={160}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(name) }}
        />
        <button className="btn" disabled={!name.trim() || busy} onClick={() => add(name)}>
          <Icon name="add" size={16} /> Add
        </button>
      </div>
      {unused.length ? (
        <div className="tagrow" style={{ marginBottom: 16 }}>
          {unused.map((c) => (
            <button className="tag-add" key={c} disabled={busy} onClick={() => add(c)}>＋ {c}</button>
          ))}
        </div>
      ) : null}

      {items.length ? items.map(([key, c]) => {
        const status = c.status ?? 'requested'
        const look = STATUSES[status] ?? REQUESTED
        const conditionId = c.condition_id
        return (
          <div className="condrow" key={conditionId ?? key}>
            <Icon name={look.icon} size={19} style={{ color: look.color }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{key}</span>
              <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                {c.received_at
                  ? `Received ${shortDate(c.received_at)}`
                  : c.requested_at
                    ? `Requested ${shortDate(c.requested_at)} · blocks underwriting`
                    : 'Not requested yet'}
                {c.description && noting !== conditionId ? ` · ${c.description}` : ''}
              </span>
              {noting === conditionId && conditionId ? (
                <input
                  className="field"
                  autoFocus
                  style={{ marginTop: 6 }}
                  placeholder="Note — what was asked for, or why it was waived"
                  maxLength={2000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onBlur={() => saveNote(conditionId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveNote(conditionId)
                    if (e.key === 'Escape') setNoting(null)
                  }}
                />
              ) : null}
            </span>
            {conditionId ? (
              <>
                <select
                  className="field"
                  style={{ width: 118 }}
                  disabled={busy}
                  value={status}
                  onChange={(e) => run(api.updateCondition(conditionId, { status: e.target.value }))}
                >
                  {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <button className="icon-btn-plain" title="Add a note"
                  onClick={() => { setNote(c.description ?? ''); setNoting(conditionId) }}>
                  <Icon name="edit_note" size={18} />
                </button>
                <button className="icon-btn-plain" title="Create a follow-up task for this condition"
                  onClick={() => onChase(key)}>
                  <Icon name="add_task" size={17} />
                </button>
                <button className="icon-btn-plain" title="Delete this condition" disabled={busy}
                  onClick={async () => {
                    if (await confirm({ title: `Delete “${key}”?`, body: 'Its history goes with it.',
                                        confirmLabel: 'Delete', danger: true })) {
                      run(api.deleteCondition(conditionId))
                    }
                  }}>
                  <Icon name="delete" size={17} />
                </button>
              </>
            ) : (
              <span className={`badge ${look.badge}`}>{label(statuses, status) ?? status}</span>
            )}
          </div>
        )
      }) : <p className="quiet">Nothing outstanding — add what you are waiting on above.</p>}
    </>
  )
}
